import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { formatDate, formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  Calendar, 
  CheckCircle, 
  Clock, 
  Download, 
  Receipt, 
  RefreshCw, 
  Send,
  AlertCircle,
  Filter,
  Search,
  DollarSign,
  Calculator
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { StatusBadge } from '@/components/common/StatusBadge';
import { Progress } from '@/components/ui/progress';

// Função para gerar um ID de fatura no formato 366131-987
const generateInvoiceDisplayId = (): string => {
  // Gerar números aleatórios para a primeira parte (6 dígitos)
  let firstPart = '';
  for (let i = 0; i < 6; i++) {
    firstPart += Math.floor(Math.random() * 10).toString();
  }
  
  // Gerar números aleatórios para a segunda parte (3 dígitos)
  let secondPart = '';
  for (let i = 0; i < 3; i++) {
    secondPart += Math.floor(Math.random() * 10).toString();
  }
  
  // Combinar as partes com o hífen
  return `${firstPart}-${secondPart}`;
};

// Função para formatar o número da fatura ou extrair um ID no formato 366131-987
const formatInvoiceNumber = (invoiceNumber: string): string => {
  // Se já estiver no formato correto (apenas dígitos e hífen), retorne como está
  if (/^\d{6}-\d{3}$/.test(invoiceNumber)) {
    return invoiceNumber;
  }
  
  // Extrair todos os números da string
  const numbers = invoiceNumber.replace(/\D/g, '');
  if (numbers.length >= 9) {
    return `${numbers.substring(0, 6)}-${numbers.substring(6, 9)}`;
  }
  
  // Caso não encontre um padrão ou não consiga formatar, gere um novo ID
  return generateInvoiceDisplayId();
};

interface Invoice {
  invoice_number: string;
  invoice_date: string;
  due_date: string | null;
  paid_date: string | null;
  status: string;
  currency: string;
  total_amount: number;
  customer_name: string;
}

interface InvoiceStats {
  total: number;
  paid: number;
  pending: number;
  overdue: number;
  totalAmount: number;
  pendingAmount: number;
}

const Invoices = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isBulkInvoicing, setIsBulkInvoicing] = useState(false);
  const [stats, setStats] = useState<InvoiceStats>({
    total: 0,
    paid: 0,
    pending: 0,
    overdue: 0,
    totalAmount: 0,
    pendingAmount: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalInvoices, setTotalInvoices] = useState(0);

  useEffect(() => {
    fetchInvoices(1, pageSize);
  }, [statusFilter]);

  const fetchInvoices = async (page: number, size: number, currentSearchTerm: string = searchTerm) => {
    setLoading(true);
    try {
      const from = (page - 1) * size;
      const to = page * size - 1;

      let query = supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          invoice_date,
          due_date,
          paid_date,
          status,
          currency,
          amount_due,
          subscription:subscription_id (
            id,
            customer_relationship:customer_relationship_id (
              id,
              customer:customer_id (
                id,
                name
              )
            )
          )
        `, { count: 'exact' });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (currentSearchTerm) {
        const searchLower = `%${currentSearchTerm.toLowerCase()}%`;
        query = query.ilike('invoice_number', searchLower);
      }

      query = query.order('invoice_date', { ascending: false }).range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      const invoiceList = data?.map(item => {
        let customerName = 'Cliente desconhecido';
        const subscription = item.subscription as any;
        if (subscription?.customer_relationship?.customer?.name) {
          customerName = subscription.customer_relationship.customer.name;
        }
        return {
          invoice_number: item.invoice_number,
          invoice_date: item.invoice_date,
          due_date: item.due_date,
          paid_date: item.paid_date,
          status: item.status,
          currency: item.currency,
          total_amount: item.amount_due,
          customer_name: customerName
        };
      }) || [];

      setInvoices(invoiceList);
      setTotalInvoices(count ?? 0);
      setCurrentPage(page);
      setPageSize(size);
      
      const paidOnPage = invoiceList.filter(inv => inv.status.toLowerCase() === 'paid').length;
      const pendingOnPage = invoiceList.filter(inv => inv.status.toLowerCase() === 'pending').length;
      const overdueOnPage = invoiceList.filter(inv => inv.status.toLowerCase() === 'overdue').length;
      
      const totalAmountOnPage = invoiceList.reduce((sum, inv) => sum + inv.total_amount, 0);
      const pendingAmountOnPage = invoiceList
        .filter(inv => ['pending', 'overdue'].includes(inv.status.toLowerCase()))
        .reduce((sum, inv) => sum + inv.total_amount, 0);

      setStats({
        total: count ?? 0,
        paid: paidOnPage,
        pending: pendingOnPage,
        overdue: overdueOnPage,
        totalAmount: totalAmountOnPage,
        pendingAmount: pendingAmountOnPage
      });

    } catch (error) {
      console.error('Erro ao buscar faturas:', error);
      toast.error('Não foi possível carregar as faturas');
      setInvoices([]);
      setTotalInvoices(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = () => {
    fetchInvoices(1, pageSize, searchTerm);
  };

  const handleStatusFilterChange = (value: string) => {
     setStatusFilter(value);
  };

  const handleTabChange = (value: string) => {
    setStatusFilter(value);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > Math.ceil(totalInvoices / pageSize)) return;
    fetchInvoices(page, pageSize);
  };

  const filteredInvoices = searchTerm 
    ? invoices.filter(invoice => 
        invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.customer_name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : invoices;

  const handleCreateInvoice = () => {
    toast.success('Nova fatura será criada com ID no formato 366131-987', {
      description: `Exemplo: ${generateInvoiceDisplayId()}`,
      duration: 5000,
    });
  };

  const handleGenerateBulkInvoices = async () => {
    try {
      setIsBulkInvoicing(true);
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);
      
      const { data: billingItems, error: billingItemsError } = await supabase
        .from('subscriptions_billing')
        .select('*')
        .eq('subscription_item_state', 'active')
        .gte('subscription_item_next_billing_at', todayStart.toISOString())
        .lte('subscription_item_next_billing_at', todayEnd.toISOString());
      
      if (billingItemsError) throw billingItemsError;
      
      if (!billingItems || billingItems.length === 0) {
        toast.info('Não há assinaturas para faturar hoje.');
        return;
      }
      
      const subscriptionGroups: Record<string, any[]> = {};
      billingItems.forEach(item => {
        if (!subscriptionGroups[item.subscription_id]) {
          subscriptionGroups[item.subscription_id] = [];
        }
        subscriptionGroups[item.subscription_id].push(item);
      });
      
      const results = [];
      for (const [subscriptionId, items] of Object.entries(subscriptionGroups)) {
        try {
          const totalAmount = items.reduce((sum, item) => sum + (item.subscription_item_quantity * item.subscription_item_unit_price), 0);
          const currencyCode = items[0].subscription_currency_code || 'BRL';
          const invoiceNumber = generateInvoiceDisplayId();
          const invoiceDate = new Date().toISOString();
          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + 30);
          
          const { data: invoice, error: invoiceError } = await supabase
            .from('invoices')
            .insert({
              subscription_id: subscriptionId,
              invoice_number: invoiceNumber,
              invoice_date: invoiceDate,
              amount_due: totalAmount,
              currency: currencyCode,
              due_date: dueDate.toISOString(),
              status: 'pending',
              created_by: 'automatic',
              updated_by: 'automatic'
            })
            .select().single();
          
          if (invoiceError) throw invoiceError;
          
          const invoiceItemsToInsert = items.map(item => ({
            invoice_id: invoice.id,
            subscription_plan_id: item.subscription_plan_id,
            quantity: item.subscription_item_quantity,
            unit_price: item.subscription_item_unit_price,
            currency_code: currencyCode,
            amount_price: item.subscription_item_amount_price || (item.subscription_item_quantity * item.subscription_item_unit_price),
            created_by: 'automatic',
            updated_by: 'automatic'
          }));
          
          const { error: invoiceItemsError } = await supabase.from('invoice_items').insert(invoiceItemsToInsert);
          if (invoiceItemsError) throw invoiceItemsError;
          
          const { error: orderError } = await supabase.from('orders').insert({
            subscription_id: subscriptionId,
            operation_type: 'faturamento',
            operation_date: new Date().toISOString(),
          });
          if (orderError) console.error('Erro ao registrar operação:', orderError);

          for (const item of items) {
            let nextBillingDate = new Date();
            const billingPeriodType = item.subscription_item_billing_by || 'monthly';
            if (billingPeriodType === 'monthly') nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
            else if (billingPeriodType === 'quarterly') nextBillingDate.setMonth(nextBillingDate.getMonth() + 3);
            else if (billingPeriodType === 'semiannually') nextBillingDate.setMonth(nextBillingDate.getMonth() + 6);
            else if (billingPeriodType === 'yearly') nextBillingDate.setFullYear(nextBillingDate.getFullYear() + 1);
            
            const { error: updateError } = await supabase.from('subscription_items').update({
              next_billing_at: nextBillingDate.toISOString(),
              updated_at: new Date().toISOString(),
              updated_by: 'automatic'
            }).eq('id', item.subscription_item_id);
            if (updateError) console.error(`Erro ao atualizar item ${item.subscription_item_id}:`, updateError);
          }
          
          results.push({ subscriptionId, success: true, message: `Fatura ${invoiceNumber} gerada com sucesso` });
        } catch (error) {
          results.push({ subscriptionId, success: false, message: error instanceof Error ? error.message : 'Erro desconhecido' });
        }
      }
      
      const successCount = results.filter(r => r.success).length;
      const failedCount = results.filter(r => !r.success).length;
      
      if (successCount > 0) toast.success(`${successCount} faturas geradas com sucesso.`);
      if (failedCount > 0) toast.error(`Falha ao gerar ${failedCount} faturas.`);
      
      fetchInvoices(currentPage, pageSize);
    } catch (error) {
      toast.error('Erro ao gerar faturas em massa: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
    } finally {
      setIsBulkInvoicing(false);
    }
  };

  const renderInvoiceTable = (invoicesToRender: Invoice[]) => (
    <Card>
      <CardContent className="p-0">
        <div className="rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Número</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead>Valor</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8"><div className="flex justify-center items-center"><RefreshCw className="h-5 w-5 animate-spin mr-2" />Carregando faturas...</div></TableCell></TableRow>
              ) : invoicesToRender.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10"><div className="text-muted-foreground">Nenhuma fatura encontrada</div></TableCell></TableRow>
              ) : (
                invoicesToRender.map((invoice) => (
                  <TableRow key={invoice.invoice_number}>
                    <TableCell className="font-extrabold">
                      <Button variant="link" className="p-0 h-auto font-extrabold text-left" onClick={() => navigate(`/invoices/${invoice.invoice_number}`)}>
                        {formatInvoiceNumber(invoice.invoice_number)}
                      </Button>
                    </TableCell>
                    <TableCell>{invoice.customer_name}</TableCell>
                    <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                    <TableCell>{invoice.due_date ? formatDate(invoice.due_date) : '-'}</TableCell>
                    <TableCell>{formatCurrency(invoice.total_amount, invoice.currency)}</TableCell>
                    <TableCell><StatusBadge status={invoice.status} context="invoice" /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/invoices/${invoice.invoice_number}`)} title="Ver detalhes da fatura"><Receipt className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Enviar fatura por email"><Send className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Baixar fatura em PDF"><Download className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Faturas</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
          {/* SVG Background Element */}
          <div className="absolute inset-0 pointer-events-none">
            <svg 
              className="opacity-30"
              width="1690"
              height="1026.5433"
              viewBox="0 0 826.54 502.06" 
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid slice"
            >
              <g transform="scale(1.1) translate(-70, -315)">
                <path d="M0 0h826.54v502.06H0z" fill="none"/>
                <path d="M0 0h826.54v502.06H0z" fill="url(#a)"/>
                <defs>
                  <linearGradient id="a" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity=".1"/>
                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity=".1"/>
                  </linearGradient>
                </defs>
              </g>
            </svg>
          </div>
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          <div className="relative container mx-auto px-6 py-6 pb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg"><Receipt className="h-6 w-6 text-white" /></div>
                  <div>
                    <h1 className="text-2xl font-extrabold text-white">Faturas</h1>
                    <p className="text-blue-100 text-base pb-2">Gerencie todas as faturas do sistema</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" onClick={() => fetchInvoices(currentPage, pageSize)} className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200">
                  <RefreshCw className="h-4 w-4 mr-2" /> Atualizar
                </Button>
                <Button onClick={handleGenerateBulkInvoices} disabled={isBulkInvoicing} className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg">
                  <Calculator className="h-4 w-4 mr-2" /> {isBulkInvoicing ? 'Gerando...' : 'Gerar em Massa'}
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="pl-3 lg:pl-4">
          <div className="container mx-auto px-6 -mt-4 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-blue-100 rounded-md"><Receipt className="h-4 w-4 text-blue-600" /></div>
                      <span className="text-sm font-extrabold text-gray-600">Total de Faturas</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{stats.total}</div>
                  </div>
                  <Progress value={stats.total > 0 ? (stats.paid / stats.total) * 100 : 0} className="h-1.5" />
                  <p className="text-xs text-gray-500 mt-2">{stats.paid} de {stats.total} pagas</p>
                </CardContent>
              </Card>
              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-green-100 rounded-md"><DollarSign className="h-4 w-4 text-green-600" /></div>
                      <span className="text-sm font-extrabold text-gray-600">Valor Total Pago</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{formatCurrency(stats.totalAmount)}</div>
                  </div>
                  <p className="text-xs text-gray-500">Todos os períodos</p>
                </CardContent>
              </Card>
              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-yellow-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-yellow-100 rounded-md"><Clock className="h-4 w-4 text-yellow-600" /></div>
                      <span className="text-sm font-extrabold text-gray-600">Pendentes</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{stats.pending}</div>
                  </div>
                  <p className="text-xs text-gray-500">{formatCurrency(stats.pendingAmount)} a receber</p>
                </CardContent>
              </Card>
              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-red-50 to-white">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 bg-red-100 rounded-md"><AlertCircle className="h-4 w-4 text-red-600" /></div>
                      <span className="text-sm font-extrabold text-gray-600">Atrasadas</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{stats.overdue}</div>
                  </div>
                  <p className="text-xs text-red-600">Requer atenção</p>
                </CardContent>
              </Card>
            </div>

            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <div className="flex flex-col md:flex-row gap-4 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Buscar por número ou cliente..." value={searchTerm} onChange={handleSearchChange} onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()} className="pl-10 w-full h-10 bg-white" />
                  </div>
                  <div className="flex gap-2">
                    <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                      <SelectTrigger className="w-[180px] h-10 bg-white"><Filter className="h-4 w-4 mr-2 text-muted-foreground" /><SelectValue placeholder="Filtrar por status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="paid">Pago</SelectItem>
                        <SelectItem value="pending">Pendente</SelectItem>
                        <SelectItem value="overdue">Atrasado</SelectItem>
                        <SelectItem value="canceled">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={handleSearchSubmit} className="h-10">Aplicar</Button>
                  </div>
                </div>
                <Tabs defaultValue="all" className="w-full" value={statusFilter} onValueChange={handleTabChange}>
                  <TabsList>
                    <TabsTrigger value="all">Todas</TabsTrigger>
                    <TabsTrigger value="pending">Pendentes</TabsTrigger>
                    <TabsTrigger value="paid">Pagas</TabsTrigger>
                    <TabsTrigger value="overdue">Atrasadas</TabsTrigger>
                  </TabsList>
                </Tabs>
              </CardHeader>
              <CardContent>
                {renderInvoiceTable(filteredInvoices)}
                {totalInvoices > 0 && (
                  <div className="flex items-center justify-center py-4">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem><PaginationPrevious onClick={() => handlePageChange(currentPage - 1)} className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
                        {currentPage > 2 && <PaginationItem><PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink></PaginationItem>}
                        {currentPage > 3 && <PaginationItem><PaginationEllipsis /></PaginationItem>}
                        {currentPage > 1 && <PaginationItem><PaginationLink onClick={() => handlePageChange(currentPage - 1)}>{currentPage - 1}</PaginationLink></PaginationItem>}
                        <PaginationItem><PaginationLink isActive>{currentPage}</PaginationLink></PaginationItem>
                        {currentPage < Math.ceil(totalInvoices / pageSize) && <PaginationItem><PaginationLink onClick={() => handlePageChange(currentPage + 1)}>{currentPage + 1}</PaginationLink></PaginationItem>}
                        {currentPage < Math.ceil(totalInvoices / pageSize) - 2 && <PaginationItem><PaginationEllipsis /></PaginationItem>}
                        {currentPage < Math.ceil(totalInvoices / pageSize) - 1 && <PaginationItem><PaginationLink onClick={() => handlePageChange(Math.ceil(totalInvoices / pageSize))}>{Math.ceil(totalInvoices / pageSize)}</PaginationLink></PaginationItem>}
                        <PaginationItem><PaginationNext onClick={() => handlePageChange(currentPage + 1)} className={currentPage >= Math.ceil(totalInvoices / pageSize) ? "pointer-events-none opacity-50" : "cursor-pointer"} /></PaginationItem>
                      </PaginationContent>
                    </Pagination>
                    <div className="ml-4 text-sm text-muted-foreground">
                      Página {currentPage} de {Math.max(1, Math.ceil(totalInvoices / pageSize))}, mostrando {invoices.length} de {totalInvoices} registros
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            <div className="h-16" />
          </div>
        </div>
      </div>
    </>
  );
};

export default Invoices;